import React, { useState, useEffect } from 'react';
import {
  Dropdown,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  DropdownSeparator,
  DrawerProvider,
} from 'equal-ds-ui';
import { ChevronDown, KeyRound, LogOut } from 'lucide-react';
import { authAPI } from '../services/auth-api.service';
import { ResetPasswordDrawer } from './ResetPasswordDrawer';
import './Header.css';

// Helper to get user info from sessionStorage (mimics Angular logic)
function getUserInfo() {
  try {
    const userinfoList = sessionStorage.getItem('loggedUserInfo');
    if (!userinfoList) return null;
    const userinfo = JSON.parse(atob(userinfoList));
    return userinfo;
  } catch (error) {
    console.error('React Header - Error parsing user info from sessionStorage:', error);
    return null;
  }
}

const Header: React.FC = () => {
  // State for header
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState('');
  const [lastLoggedIn, setLastLoggedIn] = useState('');
  const [userDetails, setUserDetails] = useState(false);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);

  // Load initial state from sessionStorage (mimics Angular ngOnInit)
  useEffect(() => {
    // User info
    const userinfo = getUserInfo();
    if (userinfo) {
      setUserName(userinfo['name'] || '');
      setUserRole(userinfo['role_name'] || userinfo['role'] || '');
      setLastLoggedIn(userinfo['last_logedIn'] || '');
      setUserDetails(true);
    } else {
      setUserDetails(false);
    }
  }, []);

  // Get user initials for avatar
  const getUserInitial = () => {
    return userName ? userName.charAt(0).toUpperCase() : 'U';
  };

  // Handle Reset Password
  const handleResetPassword = () => {
    setIsResetPasswordOpen(true);
  };

  // Logout
  const handleLogout = async () => {
    // Call logout API first (before clearing session storage)
    await authAPI.logout();

    // Clear session storage after API call
    sessionStorage.removeItem('finpro_k_Authorization');
    sessionStorage.removeItem('loggedUserInfo');
    // Note: isReactSidebarEnabled is kept to maintain user preference across sessions
    sessionStorage.removeItem('sidebarCollapsed');

    // Redirect to login page (matching Angular logout logic)
    // Use hash-based routing and preserve base path (e.g., /axismf/, /flexpay/)
    const basePath = window.location.pathname; // e.g., "/flexpay/" or "/"
    const newUI = sessionStorage.getItem('isReactLoginEnabled') === 'true';
    const targetUrl = basePath + (newUI ? '#/login' : '#/site');

    // Full page reload to ensure proper React component unmount/remount
    window.location.href = targetUrl;
  };


  return (
    <>
      <header className="header react-header-distinct">
        <div className="header-main">
          {userDetails ? (
            <div className="equal-ds-root">
              <Dropdown>
              <DropdownTrigger asChild>
                <div className="user-profile-trigger">
                  <div className="user-info">
                    <div className="user-name">{userName}</div>
                    <div className="user-role">{userRole}</div>
                  </div>
                  <div className="user-avatar-circle">
                    {getUserInitial()}
                  </div>
                  <ChevronDown className="w-4 h-4 text-text-tertiary" />
                </div>
              </DropdownTrigger>
              <DropdownContent
                align="end"
                sideOffset={8}
                className="min-w-[280px] bg-white shadow-xl rounded-xl border border-border-default p-2"
              >
                {/* User Info Header */}
                <div className="px-3 py-3 mb-1">
                  <div className="flex items-center gap-3">
                    <div className="user-avatar-circle-large">
                      {getUserInitial()}
                    </div>
                    <div>
                      <div className="font-semibold text-base text-text-primary">{userName}</div>
                      <div className="text-sm text-text-secondary italic">{userRole}</div>
                    </div>
                  </div>
                </div>

                <DropdownSeparator className="my-1" />

                {/* Reset Password */}
                <DropdownItem
                  onClick={handleResetPassword}
                  className="cursor-pointer px-3 py-2.5 flex items-center gap-3 hover:bg-background-tertiary rounded-lg transition-colors"
                >
                  <KeyRound className="w-4 h-4 text-text-tertiary" />
                  <span className="text-sm text-text-primary">Reset Password</span>
                </DropdownItem>

                {/* Sign Out */}
                <DropdownItem
                  onClick={handleLogout}
                  className="cursor-pointer px-3 py-2.5 flex items-center gap-3 hover:bg-error-50 rounded-lg transition-colors"
                >
                  <LogOut className="w-4 h-4 text-error-100" />
                  <span className="text-sm text-error-100 font-medium">Sign Out</span>
                </DropdownItem>

                {/* Last Login */}
                {lastLoggedIn && (
                  <>
                    <DropdownSeparator className="my-1" />
                    <div className="px-3 py-2 text-xs text-text-secondary">
                      Last Login: <span className="font-medium">{lastLoggedIn}</span>
                    </div>
                  </>
                )}
              </DropdownContent>
            </Dropdown>
          </div>
        ) : (
          <button className="faq-btn">FAQs</button>
        )}
        </div>
      </header>

      {/* Reset Password Drawer */}
      <DrawerProvider
        side="right"
        open={isResetPasswordOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsResetPasswordOpen(false);
          }
        }}
      >
        <div className="equal-ds-root">
          <ResetPasswordDrawer
            isOpen={isResetPasswordOpen}
            onClose={() => setIsResetPasswordOpen(false)}
            onSuccess={() => {
              setIsResetPasswordOpen(false);
            }}
          />
        </div>
      </DrawerProvider>
    </>
  );
};

export default Header;
